import React, { ReactNode, useContext, useMemo } from "react";

import { FilterFilled, PartitionOutlined, ShareAltOutlined } from "@ant-design/icons";
import { Space, Table, Tooltip, Typography, Modal } from "antd";
import { DeleteOutlined } from "@ant-design/icons";
import { ColumnType } from "antd/lib/table";
import { compareAsc } from "date-fns";

import {
  AscDesc,
  buildFilter,
  ClientValue,
  MatterFilter,
  MatterStatus,
  Sorter,
  User,
} from "../../data-types";
import { MatterStatusSelectorDropdown } from "../matter-status-selector-dropdown";
import { FilterDropdown } from "./filter-dropdown";
import { Icons } from "../vpd-icons/icons";

import styles from "./matter-table.module.css";
import { SorterResult } from "antd/lib/table/interface";
import { GlobalContext } from "../../../context/global-context";
import { UserSelectContainer } from "../../../containers/matter-table/user-select";
import { usePermissionCheck, Permission, useCurrentUserData } from "~/hooks";
import { CurrentUserFeeEarnerDataQuery, FilterMattersQuery } from "~/gql/graphql";

const { ["table-row"]: tableRowClassName, ["status-cell"]: statusCellClassName } = styles as any;

export type MatterTableFilterNames = "title" | "client" | "updatedAt" | "createdBy";
export type MatterTableFilters = Record<
  keyof Pick<MatterRecordType, MatterTableFilterNames>,
  FilterDropdown | undefined
>;

type MatterRecordType = FilterMattersQuery["filteredMatters"]["matters"][number];

export type MatterTableProps = {
  emptyMessage?: string;
  matters: MatterRecordType[];
  loading: boolean;
  onChange?: (filter: MatterFilter) => void;
  onRowClick?: (row: MatterRecordType) => void;
  filters: MatterTableFilters;
  renderActions?: (record: MatterRecordType) => ReactNode;
  updateMatter: (
    matter: Pick<
      FilterMattersQuery["filteredMatters"]["matters"][number],
      "id" | "status" | "isParent"
    >
  ) => Promise<void>;
  deleteMatter: (
    matter: Pick<
      FilterMattersQuery["filteredMatters"]["matters"][number],
      "id" | "status" | "isParent"
    >
  ) => Promise<void>;
  pagination: { total: number; current: number; pageSize: number };
  filterData: MatterFilter;
  defaultSortOrder?: AscDesc;
};

const getSorter = (
  sorterRaw: SorterResult<MatterRecordType> | SorterResult<MatterRecordType>[]
) => {
  const latestSorter = Array.isArray(sorterRaw) ? sorterRaw[sorterRaw.length - 1] : sorterRaw;
  const sorter = {
    field: latestSorter.field,
    order: "descend" === latestSorter.order ? "desc" : "asc",
  } as Sorter;
  return sorter;
};

export const MatterTable: React.FC<MatterTableProps> = ({
  emptyMessage,
  matters,
  loading,
  onChange,
  onRowClick,
  filters,
  renderActions,
  updateMatter,
  deleteMatter,
  pagination,
  filterData,
  defaultSortOrder = AscDesc.desc,
}) => {
  const [showModal, setShowModal] = React.useState(false);
  const [selectedMatter, setSelectedMatter] = React.useState<MatterRecordType | null>(null);
  const { currentUser } = useCurrentUserData({ cache: true });

  const { status } = filterData;

  const { pmsName } = useContext(GlobalContext);

  const showDelete = usePermissionCheck(Permission.UPDATE_CONFIG);

  const onChangeHandler = (
    pagination: any,
    filtersRaw: Partial<MatterTableFilters>,
    sorter: Sorter
  ) => {
    const filters = {
      ...filterData,
      ...filtersRaw,
      status,
    };
    onChange && onChange(buildFilter(pagination, filters, sorter));
  };
  const columns = useMemo(
    () =>
      defineTableColumns(
        filters,
        renderActions,
        updateMatter,
        deleteMatter,
        setShowModal,
        setSelectedMatter,
        currentUser,
        pmsName,
        filterData.createdBy ?? [],
        showDelete,
        defaultSortOrder
      ),
    [filters, renderActions, updateMatter]
  );

  const isFilterApplied = hasFilterDefined(filters);
  const emptyText = emptyMessage || "No matters found";
  const filterEmptyText = emptyMessage || "No matters found with selected filters.";
  return (
    <>
      <div style={{ display: "flex", flexDirection: "column", maxHeight: "70vh" }}>
        <Table
          onChange={(pagination, filters, sorterRaw) => {
            const sorter = getSorter(sorterRaw);
            return onChangeHandler(pagination, filters, sorter);
          }}
          onRow={(record) => ({
            onClick: () => onRowClick?.(record),
          })}
          columns={columns}
          dataSource={matters}
          loading={loading}
          rowKey="reference"
          locale={{
            // filterEmptyText message is not shown when filters are applied, so applying here to emptyText
            emptyText: isFilterApplied ? filterEmptyText : emptyText,
            filterEmptyText,
          }}
          sticky
          pagination={pagination}
          rowClassName={tableRowClassName}
          scroll={{ y: "calc(70vh - 55px)" }}
        />
      </div>
      <Modal
        title="Delete Proposal"
        open={showModal}
        onOk={() => {
          if (selectedMatter) {
            deleteMatter(selectedMatter);
          }
          setShowModal(false);
          setSelectedMatter(null);
        }}
        onCancel={() => {
          setShowModal(false);
          setSelectedMatter(null);
        }}
        okText="Delete"
      >
        <Typography.Text>
          Are you sure you want to delete{" "}
          {selectedMatter ? `"${selectedMatter?.title}"` : "this proposal"}?
        </Typography.Text>
      </Modal>
    </>
  );
};

function defineTableColumns(
  filters: MatterTableFilters,
  renderActions: ((record: MatterRecordType) => React.ReactNode) | undefined,
  updateMatter: (matter: Pick<MatterRecordType, "id" | "status" | "isParent">) => Promise<void>,
  deleteMatter: (matter: Pick<MatterRecordType, "id" | "status" | "isParent">) => Promise<void>,
  setShowModal: (showModal: boolean) => void,
  setSelectedMatter: (matter: MatterRecordType | null) => void,
  currentUser: CurrentUserFeeEarnerDataQuery["currentUserFeeEarnerData"],
  pmsName: string,
  defaultCreatedById: number[],
  showDelete: boolean,
  defaultSortOrder: AscDesc = AscDesc.desc
): ColumnType<MatterRecordType>[] {
  const {
    title: titleFilterDropdown,
    client: clientFilterDropdown,
    updatedAt: updatedAtFilterDropdown,
  } = filters || {};

  return [
    {
      title: "Title",
      dataIndex: "title",
      key: "title",
      ellipsis: true,
      render: (_, { title, externalMatterId, isParent, collaborators }) => {
        return (
          <Typography.Text>
            <Space>
              {!externalMatterId && !isParent && (
                <Tooltip
                  title={`This proposal is not linked to a ${pmsName} matter`}
                  placement="bottomRight"
                >
                  {Icons.Error}
                </Tooltip>
              )}
              {isParent && (
                <Tooltip title={`This is a multi discipline proposal`} placement="bottomRight">
                  {<PartitionOutlined />}
                </Tooltip>
              )}
              {title}
              {isSharedBy(collaborators, currentUser) && (
                <Tooltip
                  title={getSharedByText(collaborators, currentUser)}
                  placement="bottomRight"
                >
                  <ShareAltOutlined style={{ marginLeft: 5, marginRight: 5 }} />
                </Tooltip>
              )}
            </Space>
          </Typography.Text>
        );
      },
      ...createFilterProps(titleFilterDropdown),
    },
    {
      title: "Client",
      dataIndex: "client",
      key: "client",
      ellipsis: true,
      render: (client: ClientValue) => (client ? client.name : undefined),
      ...createFilterProps(clientFilterDropdown),
      sorter: (a, b) => (a?.client?.name ?? "").localeCompare(b?.client?.name ?? "") ?? 0,
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      width: "15rem",
      render: (
        status: MatterStatus,
        {
          id,
          reference,
          isParent,
          externalMatterId,
        }: Pick<MatterRecordType, "id" | "reference" | "isParent" | "externalMatterId">
      ) => (
        <MatterStatusSelectorDropdown
          status={status}
          id={id}
          isParent={isParent ?? false}
          reference={reference}
          onUpdate={updateMatter}
          handleLoadingInParent={true}
          externalMatterId={externalMatterId}
          pmsName={pmsName}
        />
      ),
      sorter: false,
      onCell: () => ({
        // Prevent row onClick handler from being called
        onClick: (e) => e.stopPropagation(),
      }),
      className: statusCellClassName,
    },
    {
      title: "Last Updated",
      dataIndex: "updatedAt",
      key: "updatedAt",
      width: "18rem",
      render: (value: string) => new Date(value).toLocaleDateString(),
      defaultSortOrder: defaultSortOrder === AscDesc.desc ? "descend" : "ascend",
      ...createFilterProps(updatedAtFilterDropdown),
      sorter: (a, b) => compareAsc(new Date(a.updatedAt), new Date(b.updatedAt)),
    },
    {
      title: "Created By",
      dataIndex: "createdBy",
      key: "createdBy",
      width: "18rem",
      ellipsis: true,
      render: (createdBy: User) => createdBy?.displayName,
      filterIcon: <FilterFilled style={{ fontSize: 18 }} />,
      defaultFilteredValue: defaultCreatedById,
      filtered: true,
      filterDropdown: ({ setSelectedKeys, confirm, clearFilters }) => {
        return (
          <UserSelectContainer
            defaultValue={defaultCreatedById}
            onApplyFilter={(values: number[]) => {
              setSelectedKeys(values);
              confirm();
            }}
            onClearFilter={() => {
              if (clearFilters) {
                clearFilters();
                confirm();
              }
            }}
          />
        );
      },
      sorter: (a, b) => {
        return a.createdBy?.displayName.localeCompare(b.createdBy?.displayName ?? "") ?? 0;
      },
    },
    // Type assertion needed: Ant Design v4 types expect React 18's ReactNode (without bigint/Promise)
    // but React 19 expanded ReactNode to include these. Since renderActions returns JSX elements,
    // this is safe at runtime.
    ...(renderActions
      ? ([
          {
            key: "action",
            width: "5rem",
            render: (value: any, record: any) => renderActions(record),
          },
        ] as ColumnType<MatterRecordType>[])
      : []),
    // Type assertion needed: Same React 19 vs Ant Design v4 type incompatibility as above
    ...(showDelete
      ? ([
          {
            key: "delete",
            width: "5rem",
            onCell: (record: any) => ({
              // Prevent row onClick handler from being called
              onClick: (e: any) => {
                e.stopPropagation();
                if (record.externalMatterId) {
                  return;
                }
                setSelectedMatter(record);
                setShowModal(true);
              },
              style: { cursor: record.externalMatterId ? "not-allowed" : "pointer" },
            }),
            render: (record: any) => {
              return (
                <Tooltip
                  title={
                    record.externalMatterId
                      ? "Matter must be unlinked before deleting"
                      : "Delete Proposal"
                  }
                  placement="left"
                >
                  <Typography.Link
                    style={{
                      width: "100%",
                      height: "100%",
                      cursor: record.externalMatterId ? "not-allowed" : "pointer",
                    }}
                    type="danger"
                  >
                    <DeleteOutlined />
                  </Typography.Link>
                </Tooltip>
              );
            },
          },
        ] as ColumnType<MatterRecordType>[])
      : []),
  ];
}

function createFilterProps(filterDropdown: FilterDropdown): Partial<ColumnType<MatterRecordType>> {
  return {
    filterIcon: <FilterFilled style={{ fontSize: 18 }} />,
    filterDropdown: filterDropdown,
  };
}

function hasFilterDefined(filters: MatterTableFilters) {
  return (
    filters && Object.keys(filters).filter((k) => !!filters[k as MatterTableFilterNames]).length > 0
  );
}

function isSharedBy(
  item: MatterRecordType["collaborators"],
  currentUser: CurrentUserFeeEarnerDataQuery["currentUserFeeEarnerData"]
) {
  return (item?.length ?? 0) > 0 && item?.some((x) => x.userId === currentUser?.userId);
}

function getSharedByText(
  item: MatterRecordType["collaborators"],
  currentUser: CurrentUserFeeEarnerDataQuery["currentUserFeeEarnerData"]
) {
  return `Shared by ${
    item?.find((x) => x.userId === currentUser?.userId)?.createdByUser?.displayName
  }`;
}
